import { useEffect, useState } from 'react';
import { useOutreachDrafts, useOutreachOperation } from '@discovr/features';
import type { OutreachDraft, OutreachOperation } from '@discovr/types';
import type { WizardTarget } from './wizardState';
import type { WizardRun } from '../useSendWizardSession';
import { failedTotal } from '../operationOutcomes';

/** The drafts route caps a page at 100 (MaxOutreachPageLimit). */
const DRAFTS_PAGE_LIMIT = 100;
/** Ensure accepts at most 1000 entries: ten pages of 100 always cover a batch. */

export type BatchDraftItem =
  | { entryId: string; username: string; state: 'writing' }
  | { entryId: string; username: string; state: 'ready'; draft: OutreachDraft }
  /** operationId: the operation that reported this outcome — lets a Retry tell a stale failure from its own. */
  | { entryId: string; username: string; state: 'skipped' | 'failed'; reason: string; operationId?: string; retryable?: boolean; /** An older draft for this creator is still waiting, hidden from the batch. */ earlierDraft?: boolean };

export interface BatchDrafts {
  items: BatchDraftItem[];
  running: boolean;
  /** The operation poll or a drafts page failed — the step says so instead of "Writing…" forever. */
  error: boolean;
  retry: () => void;
}

function draftPageParams(page: number) {
  return { kind: 'initial' as const, page, limit: DRAFTS_PAGE_LIMIT };
}

/** Go's skip reason for a creator who already has a first-touch draft waiting and was not asked to replace it. */
const ALREADY_DRAFTED = 'already_drafted';

type Outcome = OutreachOperation['outcomes'][number] & { operationId: string };

/** Go keeps an entry's previous draft until its successor is written, so the list holds older batches' drafts too:
 *  only a draft newer than the batch's floor belongs to it. With no batch known nothing is trusted. */
export function useBatchDrafts(campaignId: string, operationId: string | null, targets: WizardTarget[], batch?: WizardRun['batch'], pickedIds: number[] = []): BatchDrafts {
  const floor = batch ? batch.floor : Infinity;
  const scope = batch?.id;
  // Accumulated across operationId swaps (Retry/remount can swap to a new, narrower
  // operation): a later operation's outcomes only ADD to/override what's known for
  // the entries IT covers, never erasing a prior terminal skip/fail for an entry the
  // new operation never touched. `outcomesKey` is the snapshot (serialized) of the
  // outcomes already folded in, so the merge below only runs — and only calls
  // setState — when the current operation actually has something new, the same
  // render-time "adjust state when an input changes" shape DraftCard's resync fix
  // uses (plain state compared during render, not an effect): no stale-then-corrected
  // flash, and no ref access during render (disallowed by this repo's
  // react-hooks/refs lint rule).
  const [accumulated, setAccumulated] = useState<{ outcomes: Map<string, Outcome>; outcomesKey: string }>({ outcomes: new Map(), outcomesKey: '' });
  const operationQuery = useOutreachOperation(campaignId, operationId);
  const operation = operationQuery.data;
  const enabled = !!operationId;
  // Unrolled, not looped: React hooks can't be called inside a loop/array
  // (Rules of Hooks — the call count and order must be identical every
  // render), so each of the up-to-10 pages a 1000-entry batch can span needs
  // its own literal useOutreachDrafts call. pageNeeded gates all but the
  // first off until a prior page's `total` says it's actually needed.
  const page1 = useOutreachDrafts(campaignId, draftPageParams(1), { enabled, scope });
  // Page N is read only when the batch's total (from page 1) is larger than the pages before it.
  const total = page1.data?.total ?? 0;
  const pageNeeded = (page: number) => enabled && total > (page - 1) * DRAFTS_PAGE_LIMIT;
  const page2 = useOutreachDrafts(campaignId, draftPageParams(2), { enabled: pageNeeded(2), scope });
  const page3 = useOutreachDrafts(campaignId, draftPageParams(3), { enabled: pageNeeded(3), scope });
  const page4 = useOutreachDrafts(campaignId, draftPageParams(4), { enabled: pageNeeded(4), scope });
  const page5 = useOutreachDrafts(campaignId, draftPageParams(5), { enabled: pageNeeded(5), scope });
  const page6 = useOutreachDrafts(campaignId, draftPageParams(6), { enabled: pageNeeded(6), scope });
  const page7 = useOutreachDrafts(campaignId, draftPageParams(7), { enabled: pageNeeded(7), scope });
  const page8 = useOutreachDrafts(campaignId, draftPageParams(8), { enabled: pageNeeded(8), scope });
  const page9 = useOutreachDrafts(campaignId, draftPageParams(9), { enabled: pageNeeded(9), scope });
  const page10 = useOutreachDrafts(campaignId, draftPageParams(10), { enabled: pageNeeded(10), scope });
  const pageQueries = [page1, page2, page3, page4, page5, page6, page7, page8, page9, page10];
  const pageEnabled = (index: number) => (index === 0 ? enabled : pageNeeded(index + 1));

  const settled = operation ? operation.appliedCount + operation.skippedCount + failedTotal(operation) : 0;

  const refetchEnabledPages = () => {
    pageQueries.forEach((query, index) => {
      if (pageEnabled(index)) void query.refetch?.();
    });
  };

  // Fallback for a dropped stream: useOutreachSSE's outreach_draft_ready is the primary refresh.
  useEffect(() => {
    if (enabled && settled > 0) refetchEnabledPages();
    // Runs on the settled count only; the refetch functions are fresh each render and read the current pages.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, settled]);

  const retry = () => {
    void operationQuery.refetch?.();
    refetchEnabledPages();
  };

  const error = enabled && (!!operationQuery.isError || pageQueries.some((query) => !!query.isError));

  if (!enabled) {
    if (accumulated.outcomesKey !== '') setAccumulated({ outcomes: new Map(), outcomesKey: '' });
  } else if (operation) {
    // Keying on outcomes content alone is not enough: a retry operation whose
    // outcomes happen to serialize identically to the one it replaced (e.g. the
    // same single-entry failure, same reason text) would otherwise look
    // unchanged and skip the merge — leaving that entry's accumulated outcome
    // tagged with the OLD operation's id forever, so a consumer keyed on "is
    // this outcome from MY retry" (DraftsStep's stale-Retry guard) can never
    // tell the retry actually landed.
    const key = `${operation.id}:${JSON.stringify(operation.outcomes)}`;
    if (key !== accumulated.outcomesKey) {
      const merged = new Map(accumulated.outcomes);
      for (const o of operation.outcomes) merged.set(o.itemKey, { ...o, operationId: operation.id });
      setAccumulated({ outcomes: merged, outcomesKey: key });
    }
  }
  const outcomes = accumulated.outcomes;
  // An applied outcome names the draft it parked: only that one is this batch's. Without one (Template, Retry) the floor decides.
  const belongsToBatch = (d: OutreachDraft) => {
    // A draft the admin picked from the draft menu is the creator's current one, whatever its age.
    if (pickedIds.includes(d.message.id)) return true;
    const outcome = outcomes.get(d.entry.entryId);
    // Reopened with a draft already waiting: the batch left it alone (nothing redrafted), so it is the one to show, edits kept.
    if (outcome?.outcome === 'skipped' && outcome.reason === ALREADY_DRAFTED) return true;
    if (d.message.id <= floor) return false;
    return outcome?.outcome !== 'applied' || outcome.messageId === undefined || outcome.messageId === d.message.id;
  };
  const byEntry = new Map<string, OutreachDraft>();
  const earlierDrafts = new Set<string>();
  for (const query of pageQueries) {
    for (const d of query.data?.items ?? []) {
      if (belongsToBatch(d)) byEntry.set(d.entry.entryId, d);
      else earlierDrafts.add(d.entry.entryId);
    }
  }
  const finished = !!operation && operation.status !== 'running';
  const items: BatchDraftItem[] = targets.map((t) => {
    const base = { entryId: t.entryId, username: t.username };
    const draft = byEntry.get(t.entryId);
    const outcome = outcomes.get(t.entryId);
    const earlier = earlierDrafts.has(t.entryId) ? { earlierDraft: true } : {};
    if (draft) return { ...base, state: 'ready', draft };
    if (outcome && (outcome.outcome === 'skipped' || outcome.outcome === 'failed')) {
      return { ...base, state: outcome.outcome, reason: outcome.reason ?? 'unknown', operationId: outcome.operationId, retryable: outcome.retryable, ...earlier };
    }
    // queued/exists: ensure_drafts finishes once it hands off; the nested draft_initial is still composing.
    if (outcome) return { ...base, state: 'writing' };
    // No outcome from a finished operation: genuinely lost (Ruling 40 converges it).
    if (finished) return { ...base, state: 'failed', reason: 'stalled', operationId: operation.id, ...earlier };
    return { ...base, state: 'writing' };
  });
  return { items, running: operation?.status === 'running', error, retry };
}
